import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMedia,
  uploadMedia,
  uploadMultipleMedia,
  deleteMedia,
  updateMedia,
  setFilters,
  setPage,
  clearFilters,
  toggleMediaSelection,
  clearError,
} from './mediaSlice';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import Shell from '../dashboard/Shell';
import {
  Image as ImageIcon, Video, FileText, Music, Archive, File, Play,
  Plus, RefreshCw, Edit2, Trash2, Download, LayoutGrid, List as ListIcon,
  Upload, Files, HardDrive, AlertTriangle, X, Loader2, Check,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import './Media.css';

const TYPE_OPTIONS = [
  { value: 'all',      label: 'All types' },
  { value: 'image',    label: 'Images' },
  { value: 'video',    label: 'Videos' },
  { value: 'document', label: 'Documents' },
  { value: 'audio',    label: 'Audio' },
  { value: 'archive',  label: 'Archives' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name',   label: 'Name A–Z' },
  { value: 'size',   label: 'Size' },
];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const normalizeType = (t) => (typeof t === 'string' ? t.toLowerCase() : 'document');
const isImageType = (t) => normalizeType(t) === 'image';
const isVideoType = (t) => normalizeType(t) === 'video';
const isDownloadable = (t) => {
  const n = normalizeType(t);
  return n === 'document' || n === 'archive' || n === 'audio' || n === 'video';
};

function FileTypeIcon({ type, size = 28 }) {
  switch (normalizeType(type)) {
    case 'image':    return <ImageIcon size={size} />;
    case 'video':    return <Video size={size} />;
    case 'audio':    return <Music size={size} />;
    case 'document': return <FileText size={size} />;
    case 'archive':  return <Archive size={size} />;
    default:         return <File size={size} />;
  }
}

export default function Media() {
  const dispatch = useDispatch();
  const { confirm } = useConfirm();
  const { media, loading, error, selectedMedia, filters, storage, pagination } = useSelector((s) => s.media);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');

  const singleFileInputRef = useRef(null);
  const multipleFileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchMedia({ params: filters }));
  }, [dispatch, filters]);

  // If the server reports fewer pages than the page we're currently on
  // (e.g. after a search that shrinks results, or a delete on the last
  // page), snap back to the last valid page.
  useEffect(() => {
    const currentPage = filters.page || 1;
    const totalPages = pagination?.pages || 1;
    if (currentPage > totalPages) {
      dispatch(setPage(totalPages));
    }
  }, [dispatch, filters.page, pagination?.pages]);

  const handleFileUpload = (event, multiple = false) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    if (!multiple || files.length === 1) {
      dispatch(uploadMedia({ file: files[0] }));
    } else {
      dispatch(uploadMultipleMedia({ files }));
    }
    event.target.value = '';
    setShowUploadModal(false);
  };

  const handleFilterChange = (key, value) => dispatch(setFilters({ [key]: value }));

  /* ── Selection-driven actions ────────────────────────────── */
  const selectedItems = useMemo(
    () => media.filter((m) => selectedMedia.includes(m.id)),
    [media, selectedMedia]
  );
  const selectionCount = selectedItems.length;
  const canEditSelected = selectionCount === 1;
  const canDownloadSelected =
    selectionCount > 0 && selectedItems.every((m) => isDownloadable(m.type) || isImageType(m.type));

  const clearSelection = () => {
    selectedMedia.forEach((id) => dispatch(toggleMediaSelection(id)));
  };

  const handleBulkDelete = async () => {
    if (selectionCount === 0) return;
    const ok = await confirm(
      `Delete ${selectionCount} item${selectionCount === 1 ? '' : 's'}? This cannot be undone.`,
      { title: 'Delete media?', confirmText: 'Delete', cancelText: 'Cancel', type: 'danger' }
    );
    if (!ok) return;
    selectedMedia.forEach((id) => dispatch(deleteMedia({ id })));
    clearSelection();
  };

  const handleBulkDownload = () => {
    selectedItems.forEach((m) => window.open(m.url, '_blank'));
  };

  const handleEditSelected = () => {
    if (selectionCount !== 1) return;
    setEditingMedia(selectedItems[0]);
    setShowEditModal(true);
  };

  const handleUpdateMedia = (updates) => {
    dispatch(updateMedia({ id: editingMedia.id, updates }));
    setShowEditModal(false);
    setEditingMedia(null);
  };

  /* ── Storage ─────────────────────────────────────────────── */
  const showStorage = storage && !storage.unlimited;
  const usagePercent = showStorage && storage.limitMB
    ? Math.min((storage.usedMB / storage.limitMB) * 100, 100)
    : 0;
  const isNearLimit = showStorage && usagePercent >= 70;
  const isOverLimit = showStorage && usagePercent >= 90;
  const remainingLow = showStorage && storage.remainingMB <= 50;

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <Shell>
      <div className="med-page">

        {/* ── Header ────────────────────────────────────── */}
        <header className="med-header">
          <div className="med-header-text">
            <h1 className="med-title">Media library</h1>
            <p className="med-subtitle">Manage your images, videos, and files.</p>
          </div>
          <div className="med-header-actions">
            <button
              type="button"
              className="med-btn med-btn-secondary"
              onClick={() => dispatch(fetchMedia({ params: filters }))}
            >
              <RefreshCw size={13} /> <span className="med-btn-text">Refresh</span>
            </button>
            <button
              type="button"
              className="med-btn med-btn-primary"
              onClick={() => setShowUploadModal(true)}
            >
              <Plus size={13} /> <span className="med-btn-text">Upload</span>
            </button>
          </div>
        </header>

        {/* ── Storage usage ───────────────────────────────── */}
        {showStorage && (
          <section className={`med-storage${isOverLimit ? ' is-over' : isNearLimit ? ' is-near' : ''}`}>
            <div className="med-storage-head">
              <span className="med-storage-label">
                <HardDrive size={14} />
                <strong>{storage.usedMB?.toFixed(1) || 0} MB</strong> of {storage.limitMB || 0} MB used
              </span>
              <span className="med-storage-percent">{usagePercent.toFixed(0)}%</span>
            </div>
            <div className="med-storage-track">
              <div className="med-storage-fill" style={{ width: `${usagePercent}%` }} />
            </div>
            {remainingLow && (
              <div className="med-storage-warn">
                <AlertTriangle size={14} />
                <span>
                  {storage.remainingMB <= 0
                    ? 'Storage limit reached. Upgrade your plan to upload more files.'
                    : `Only ${storage.remainingMB?.toFixed(1)} MB remaining. Consider upgrading your plan.`}
                </span>
              </div>
            )}
          </section>
        )}

        {/* ── Error alert ─────────────────────────────────── */}
        {error && (
          <div className="med-error">
            <AlertTriangle size={14} />
            <span>{error}</span>
            <button
              type="button"
              className="med-error-close"
              onClick={() => dispatch(clearError())}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Filters ─────────────────────────────────────── */}
        <div className="med-filters">
          <input
            type="text"
            className="med-input med-search-input"
            placeholder="Search media…"
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <select
            className="med-input med-select"
            value={filters.type || 'all'}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            aria-label="Filter by type"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="med-input med-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="med-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`med-view-btn${viewMode === 'grid' ? ' is-active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
              title="Grid view"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              type="button"
              className={`med-view-btn${viewMode === 'list' ? ' is-active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
              title="List view"
            >
              <ListIcon size={14} />
            </button>
          </div>
          <button
            type="button"
            className="med-btn med-btn-ghost med-filters-clear"
            onClick={() => dispatch(clearFilters())}
          >
            Clear
          </button>
        </div>

        {/* ── Bulk action bar (sticky when ≥1 selected) ──── */}
        {selectionCount > 0 && (
          <div className="med-bulk">
            <span className="med-bulk-count">
              <Check size={14} /> {selectionCount} selected
            </span>
            <div className="med-bulk-actions">
              <button
                type="button"
                className="med-btn med-btn-secondary"
                onClick={handleEditSelected}
                disabled={!canEditSelected}
                title={canEditSelected ? 'Edit' : 'Select exactly 1 item to edit'}
              >
                <Edit2 size={13} /> <span className="med-btn-text">Edit</span>
              </button>
              <button
                type="button"
                className="med-btn med-btn-secondary"
                onClick={handleBulkDownload}
                disabled={!canDownloadSelected}
              >
                <Download size={13} /> <span className="med-btn-text">Download</span>
              </button>
              <button
                type="button"
                className="med-btn med-btn-danger"
                onClick={handleBulkDelete}
              >
                <Trash2 size={13} /> <span className="med-btn-text">Delete</span>
              </button>
              <button
                type="button"
                className="med-btn med-btn-ghost"
                onClick={clearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* ── Body ────────────────────────────────────────── */}
        {loading ? (
          <div className="med-loading">
            <Loader2 size={20} className="med-spin" /> Loading media…
          </div>
        ) : media.length === 0 ? (
          <div className="med-empty">
            <div className="med-empty-icon"><Files size={22} /></div>
            <h3 className="med-empty-title">No media found</h3>
            <p className="med-empty-desc">
              Upload your first image, video, or file to get started.
            </p>
            <button
              type="button"
              className="med-btn med-btn-primary"
              onClick={() => setShowUploadModal(true)}
            >
              <Plus size={13} /> Upload media
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="med-grid">
            {media.map((item) => {
              const isSelected = selectedMedia.includes(item.id);
              const typeKey = normalizeType(item.type);
              return (
                <article
                  key={item.id}
                  className={`med-tile med-tile-${typeKey}${isSelected ? ' is-selected' : ''}`}
                  onClick={() => dispatch(toggleMediaSelection(item.id))}
                  title={item.originalName}
                >
                  {/* Preview fills the entire tile */}
                  {isImageType(item.type) ? (
                    <img
                      className="med-tile-img"
                      src={item.url}
                      alt={item.alt || item.originalName}
                      loading="lazy"
                    />
                  ) : (
                    <div className="med-tile-placeholder">
                      <FileTypeIcon type={item.type} size={28} />
                      <span className="med-tile-ext">{typeKey}</span>
                    </div>
                  )}

                  {/* Play badge for videos */}
                  {isVideoType(item.type) && (
                    <span className="med-tile-play"><Play size={14} fill="currentColor" /></span>
                  )}

                  {/* Selection checkbox — always visible */}
                  <span
                    className={`med-tile-check${isSelected ? ' is-checked' : ''}`}
                    aria-hidden="true"
                  >
                    {isSelected && <Check size={12} strokeWidth={3} />}
                  </span>

                  {/* Bottom name overlay — fades in on desktop hover, always shown on mobile */}
                  <span className="med-tile-name">
                    <span className="med-tile-name-text">{item.originalName}</span>
                  </span>
                </article>
              );
            })}
          </div>
        ) : (
          <ul className="med-list">
            {media.map((item) => {
              const isSelected = selectedMedia.includes(item.id);
              return (
                <li
                  key={item.id}
                  className={`med-row${isSelected ? ' is-selected' : ''}`}
                  onClick={() => dispatch(toggleMediaSelection(item.id))}
                >
                  <div className="med-row-grid">
                    <span
                      className={`med-row-check${isSelected ? ' is-checked' : ''}`}
                      aria-hidden="true"
                    >
                      {isSelected && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="med-row-thumb">
                      {isImageType(item.type) ? (
                        <img src={item.url} alt={item.alt || item.originalName} loading="lazy" />
                      ) : (
                        <FileTypeIcon type={item.type} size={18} />
                      )}
                    </span>
                    <span className="med-row-info">
                      <strong title={item.originalName}>{item.originalName}</strong>
                      <em>
                        {item.type} · {formatFileSize(item.size)}
                        {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
                      </em>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* ── Pagination ─────────────────────────────────── */}
        {!loading && media.length > 0 && pagination?.pages > 1 && (
          <nav className="med-pagination" aria-label="Media pagination">
            <button
              type="button"
              className="med-btn med-btn-secondary med-pagination-btn"
              onClick={() => dispatch(setPage((filters.page || 1) - 1))}
              disabled={(filters.page || 1) <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
              <span className="med-btn-text">Previous</span>
            </button>
            <span className="med-pagination-status">
              Page <strong>{filters.page || 1}</strong> of <strong>{pagination.pages}</strong>
              {typeof pagination.total === 'number' && (
                <span className="med-pagination-total"> · {pagination.total} item{pagination.total === 1 ? '' : 's'}</span>
              )}
            </span>
            <button
              type="button"
              className="med-btn med-btn-secondary med-pagination-btn"
              onClick={() => dispatch(setPage((filters.page || 1) + 1))}
              disabled={(filters.page || 1) >= pagination.pages}
              aria-label="Next page"
            >
              <span className="med-btn-text">Next</span>
              <ChevronRight size={14} />
            </button>
          </nav>
        )}

        {/* ── Upload Modal ───────────────────────────────── */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Upload media"
          size="md"
          padded={false}
          footer={
            <button
              type="button"
              className="med-btn med-btn-secondary"
              onClick={() => setShowUploadModal(false)}
            >
              Close
            </button>
          }
        >
          <div className="med-upload">
            <button
              type="button"
              className="med-drop"
              onClick={() => singleFileInputRef.current?.click()}
            >
              <span className="med-drop-icon"><Upload size={20} /></span>
              <span className="med-drop-title">Upload a file</span>
              <span className="med-drop-desc">Single file, up to 15&nbsp;MB</span>
            </button>

            <button
              type="button"
              className="med-drop"
              onClick={() => multipleFileInputRef.current?.click()}
            >
              <span className="med-drop-icon"><Files size={20} /></span>
              <span className="med-drop-title">Upload multiple files</span>
              <span className="med-drop-desc">Up to 15&nbsp;MB each</span>
            </button>

            <input
              ref={singleFileInputRef}
              type="file"
              onChange={(e) => handleFileUpload(e, false)}
              style={{ display: 'none' }}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            />
            <input
              ref={multipleFileInputRef}
              type="file"
              multiple
              onChange={(e) => handleFileUpload(e, true)}
              style={{ display: 'none' }}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            />
          </div>
        </Modal>

        {/* ── Edit Modal ─────────────────────────────────── */}
        <Modal
          isOpen={showEditModal && !!editingMedia}
          onClose={() => { setShowEditModal(false); setEditingMedia(null); }}
          title="Edit media"
          size="md"
          padded={false}
          footer={
            <>
              <button
                type="button"
                className="med-btn med-btn-secondary"
                onClick={() => { setShowEditModal(false); setEditingMedia(null); }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="med-edit-form"
                className="med-btn med-btn-primary"
              >
                Save changes
              </button>
            </>
          }
        >
          {editingMedia && (
            <EditMediaForm media={editingMedia} onSave={handleUpdateMedia} />
          )}
        </Modal>

      </div>
    </Shell>
  );
}

/* ─── Edit form ──────────────────────────────────────────────── */
function EditMediaForm({ media, onSave }) {
  const [alt, setAlt] = useState(media.alt || '');
  const [description, setDescription] = useState(media.description || '');
  const [tags, setTags] = useState(media.tags?.join(', ') || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      alt: alt.trim() || undefined,
      description: description.trim() || undefined,
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    });
  };

  return (
    <form id="med-edit-form" className="med-form" onSubmit={handleSubmit}>
      {isImageType(media.type) && (
        <div className="med-edit-preview">
          <img src={media.url} alt={media.alt || media.originalName} />
        </div>
      )}
      <div className="med-edit-meta">
        <strong>{media.originalName}</strong>
        <em>{media.type} · {formatFileSize(media.size)}</em>
      </div>
      <div className="med-field">
        <label className="med-field-label">Alt text</label>
        <input
          className="med-input"
          type="text"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Describe this media for accessibility"
        />
      </div>
      <div className="med-field">
        <label className="med-field-label">Description</label>
        <textarea
          className="med-input med-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          rows={3}
        />
      </div>
      <div className="med-field">
        <label className="med-field-label">Tags</label>
        <input
          className="med-input"
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="tag1, tag2, tag3"
        />
        <span className="med-field-hint">Separate tags with commas.</span>
      </div>
    </form>
  );
}
